// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import type { ViewId } from '../types';
import { insights, materialMovers } from '../analysis';
import { catInfo } from '../domain';
import { rankedBarsSvg } from '../components/charts';
import { escapeHtml, formatPercent, fyShort } from '../utils/format';

export function renderInsights(root: HTMLElement, ctx: ViewContext): void {
  const d = ctx.data;
  const list = insights(d);
  const movers = materialMovers(d);

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">What the numbers say</h1>
      <p class="view-sub">Findings detected automatically from the latest ${escapeHtml(fyShort(d.latestYear))} data — the standouts, the gaps
        and the trends worth knowing. Follow a link to dig into any of them.</p>
    </div>
    <div class="insight-grid">
      ${list
        .map(
          (ins) => `<div class="insight ${ins.severity}">
            <h3>${escapeHtml(ins.title)}</h3>
            <p>${escapeHtml(ins.body)}</p>
            <div class="insight-links">${ins.links
              .map((l) => `<button class="chip" data-kind="${l.kind}" data-key="${escapeHtml(l.key)}">${escapeHtml(l.label)}</button>`)
              .join('')}</div>
          </div>`,
        )
        .join('')}
    </div>

    <div class="panel" style="margin-top:var(--space-xl)">
      <div class="panel-title">Biggest recovery improvers since ${escapeHtml(fyShort(d.firstYear))}</div>
      <div class="panel-sub">Change in each material's recovery rate, in percentage points, over the ${d.years.length} reporting years. Click a bar for detail.</div>
      <div data-role="movers"></div>
    </div>
  `;

  const moversEl = root.querySelector('[data-role="movers"]') as HTMLElement;
  moversEl.innerHTML = rankedBarsSvg(
    movers.map((m) => ({
      label: catInfo(m.key).short,
      value: Math.round(m.delta),
      colour: m.delta >= 0 ? 'var(--status-good)' : 'var(--status-bad)',
      tip: `${catInfo(m.key).short}: ${formatPercent(m.first, 0)} → ${formatPercent(m.last, 0)} (${m.delta >= 0 ? '+' : ''}${m.delta.toFixed(0)} pts)`,
      data: { 'open-material': m.key },
    })),
    { width: 560, valueFmt: (v) => `${v >= 0 ? '+' : ''}${v} pts`, clickAttr: 'open-material' },
  );
  moversEl.querySelectorAll('[data-open-material]').forEach((el) => el.addEventListener('click', () => ctx.openMaterial(el.getAttribute('data-open-material') as string)));

  root.querySelectorAll('.chip[data-kind]').forEach((chip) =>
    chip.addEventListener('click', () => {
      const kind = chip.getAttribute('data-kind');
      const key = chip.getAttribute('data-key') as string;
      if (kind === 'material') ctx.openMaterial(key);
      else if (kind === 'state') ctx.openState(key);
      else if (kind === 'view') ctx.goTo(key as ViewId);
    }),
  );
}
